import { create } from "zustand";

/**
 * 客服練習模式（Practice Mode）
 * --------------------------------------------------------------------------
 * 目的：讓「客服」在任何瀏覽器反覆操作 LINE 註冊 & 綁定流程，畫面與客戶看到的
 *       一模一樣，但完全不碰真實 LINE / 後端 / 簡訊。練熟後即可在電話/對話中
 *       一步步教客戶。
 *
 * 嚴格隔離：所有練習行為都以 isPracticeMode() 為唯一開關；旗標關閉時（正式客戶）
 *           provider / axios / 地址子系統的行為與原本 100% 相同。
 *
 * 旗標存放：sessionStorage（同分頁有效、關閉分頁即失效），讓非 React 程式碼
 *           （axios 攔截器、provider、地址 fallback）可同步讀取；另用 zustand
 *           鏡射一份供 React 元件即時反應（顯示/隱藏教練面板）。
 */

export const PRACTICE_FLAG_KEY = "practiceMode";
export const PRACTICE_SCENARIO_KEY = "practiceScenario";

/** 練習模式注入的假 LINE access token（讓四步驟畫面不會去呼叫 liff.login）。 */
export const PRACTICE_FAKE_LINE_TOKEN = "PRACTICE_FAKE_LINE_TOKEN";
/** 練習模式注入的假 jwtToken。 */
export const PRACTICE_FAKE_JWT = "PRACTICE_FAKE_JWT";
/** 練習用「正確」驗證碼，會顯示在教練面板提示客服。 */
export const PRACTICE_OTP = "12345";
/** 練習用家庭邀請碼（6 位數字）。 */
export const PRACTICE_FAMILY_INVITE_CODE = "620358";

/** 練習沙盒的客戶姓名（可在帳號頁編輯；存 sessionStorage，讓假後端 /customer/home 讀到最新值）。 */
export const PRACTICE_CUSTOMER_NAME_KEY = "practiceCustomerName";
export const PRACTICE_DEFAULT_CUSTOMER_NAME = "練習客戶";
export const getPracticeCustomerName = (): string => {
  try {
    return sessionStorage.getItem(PRACTICE_CUSTOMER_NAME_KEY) || PRACTICE_DEFAULT_CUSTOMER_NAME;
  } catch {
    return PRACTICE_DEFAULT_CUSTOMER_NAME;
  }
};
export const setPracticeCustomerName = (name: string) => {
  try {
    sessionStorage.setItem(PRACTICE_CUSTOMER_NAME_KEY, name);
  } catch {
    // sessionStorage 不可用 → 略過
  }
};

export type PracticeScenario =
  | "normal"
  | "joinFamily"
  | "getInvite"
  | "orderGasShop"
  | "orderKiosk"
  | "notifyExpiring"
  | "onboardingQr"
  | "flyerKnown"
  | "flyerUnknown"
  | "firstJoinSetup"
  | "smsInvite"
  | "orders"
  | "manageAccount";

/** 情境分類（練習 Hub 依此分組顯示）。 */
export type PracticeCategory = "onboarding" | "register" | "order" | "manage";

export const PRACTICE_CATEGORIES: { key: PracticeCategory; label: string; desc: string }[] = [
  { key: "onboarding", label: "新客戶引導", desc: "掃碼 / 首次加入設定" },
  { key: "register", label: "綁定與家庭", desc: "序號綁定 / 家庭帳戶 / 邀請碼" },
  { key: "order", label: "叫瓦斯下單", desc: "瓦斯訂購 / 一鍵下單" },
  { key: "manage", label: "訂單與帳號", desc: "查訂單 / 地址 / 付款 / 優惠券" },
];

export interface ScenarioMeta {
  key: PracticeScenario;
  label: string;
  /** 進入此情境時導向的起始路由。 */
  start: string;
  /** 教練面板上方一句話描述。 */
  summary: string;
  /** 所屬分類（Hub 分組）。 */
  category: PracticeCategory;
  /** Hub 卡片上的圖示（emoji）。 */
  icon: string;
}

export const PRACTICE_SCENARIOS: ScenarioMeta[] = [
  // --- 新客戶引導 ---
  {
    key: "onboardingQr",
    label: "掃碼註冊（新客戶）",
    // 帶 shopCode：練習假後端會解析出真實 supplier_id → 判定為「掃碼·有指定瓦斯行」，
    // 走與正式相同的流程（不播配對動畫/完成頁，直接進一鍵下單）。
    start: "/onboarding?src=qr&shopCode=PRACTICE01&scan=1",
    summary: "掃鋼瓶 QR → 允許定位 → 驗證身分 → 就地註冊 → 一鍵下單。",
    category: "onboarding",
    icon: "📷",
  },
  {
    key: "flyerKnown",
    label: "傳單註冊（有指定瓦斯行）",
    start: "/onboarding?src=flyer&supplier=S001",
    summary: "傳單已指定瓦斯行：問有沒有叫過 → 手機/市話/地址逐層比對 → 比對到既有客戶或當新客戶。",
    category: "onboarding",
    icon: "📄",
  },
  {
    key: "flyerUnknown",
    label: "傳單註冊（沒指定瓦斯行）",
    start: "/onboarding?src=flyer",
    summary: "傳單沒指定：以手機/市話/地址跨瓦斯行比對 → 確認/挑選瓦斯行，或推薦附近合作瓦斯行。",
    category: "onboarding",
    icon: "🗺️",
  },
  {
    key: "firstJoinSetup",
    label: "首次加入設定",
    start: "/home",
    summary: "新客戶首次設定：身分核對 → 家中鋼瓶/配件 → 完成加入 → 開始叫瓦斯。",
    category: "onboarding",
    icon: "📝",
  },
  {
    key: "smsInvite",
    label: "簡訊邀請下單",
    start: "/sms-invite?phone=0912345678&invite=620358&order_id=ADO_PRACTICE",
    summary: "點簡訊邀請連結 → 靜默註冊 → 直接看到瓦斯行為你建立的訂單。",
    category: "onboarding",
    icon: "✉️",
  },
  // --- 綁定與家庭 ---
  { key: "normal", label: "正常綁定", start: "/register/step2/inviteCode", summary: "客戶序號 → 直接完成註冊。", category: "register", icon: "🔗" },
  { key: "joinFamily", label: "加入家庭", start: "/home", summary: "首頁 → 帳號 → 新增帳戶 → 加入既有的瓦斯帳戶 → 輸入 6 碼邀請碼。", category: "register", icon: "👨‍👩‍👧" },
  { key: "getInvite", label: "取得邀請碼", start: "/home", summary: "首頁 → 帳號 → 點帳戶 → 邀請成員 → 產生 6 碼邀請碼給對方。", category: "register", icon: "🎟️" },
  // --- 叫瓦斯下單 ---
  { key: "orderGasShop", label: "下單（瓦斯訂購）", start: "/home", summary: "首頁 → 瓦斯訂購 → 選商品 → 安裝位置 → 配送時間 → 購物車 → 確認下單。", category: "order", icon: "🛒" },
  { key: "orderKiosk", label: "下單（一鍵下單）", start: "/kiosk", summary: "一鍵下單：選購/重訂 → 購物車 → 確認 → 送出。", category: "order", icon: "⚡" },
  {
    key: "notifyExpiring",
    label: "即將到期通知下單",
    // 獨立路由（不放在 /practice/* 之下，避免被 /practice/:scenario 的 Hub 深連結接走）。
    start: "/practice-notice",
    summary: "LINE 收到「瓦斯快用完」提醒卡 → 點卡片上的立即叫瓦斯 → 一鍵下單送出。",
    category: "order",
    icon: "🔔",
  },
  // --- 訂單與帳號 ---
  { key: "orders", label: "查詢訂單", start: "/orders", summary: "查看訂單清單與進度、點入看訂單明細。", category: "manage", icon: "📦" },
  { key: "manageAccount", label: "帳號與地址", start: "/account", summary: "帳號設定、切換/新增地址、付款方式、發票、優惠券。", category: "manage", icon: "⚙️" },
];

/** 網址 slug → 情境（/practice/<slug>）。同時支援情境 key 本身與好記的別名。 */
export const SCENARIO_SLUGS: Record<string, PracticeScenario> = {
  onboarding: "onboardingQr",
  qr: "onboardingQr",
  onboardingqr: "onboardingQr",
  flyer: "flyerKnown",
  flyerknown: "flyerKnown",
  flyerunknown: "flyerUnknown",
  firstjoin: "firstJoinSetup",
  setup: "firstJoinSetup",
  firstjoinsetup: "firstJoinSetup",
  bind: "normal",
  normal: "normal",
  family: "joinFamily",
  joinfamily: "joinFamily",
  invite: "getInvite",
  getinvite: "getInvite",
  order: "orderGasShop",
  gasshop: "orderGasShop",
  ordergasshop: "orderGasShop",
  kiosk: "orderKiosk",
  orderkiosk: "orderKiosk",
  notify: "notifyExpiring",
  expiring: "notifyExpiring",
  notifyexpiring: "notifyExpiring",
  orders: "orders",
  account: "manageAccount",
  manageaccount: "manageAccount",
  sms: "smsInvite",
  smsinvite: "smsInvite",
};

/** 解析 /practice/<slug> 的 slug；找不到回 null（顯示選單 Hub）。 */
export const resolveScenarioSlug = (slug?: string): PracticeScenario | null => {
  if (!slug) return null;
  const key = SCENARIO_SLUGS[slug.toLowerCase()];
  if (key) return key;
  return PRACTICE_SCENARIOS.some((s) => s.key === slug) ? (slug as PracticeScenario) : null;
};

const isBrowser = () => typeof window !== "undefined";

/** 練習模式是否啟用（sync，非 React 程式碼也可呼叫）。 */
export const isPracticeMode = (): boolean => {
  if (!isBrowser()) return false;
  // 練習專用部署（build 旗標 REACT_APP_PRACTICE_ONLY=1，見 scripts/deploy-s3.sh 的 practice 目標）：
  // 整個 app 開機即視為練習模式。此為唯一真相來源，讓 axios 攔截器 / provider / zustand 初始化都同步。
  if (process.env.REACT_APP_PRACTICE_ONLY === "1") {
    try {
      sessionStorage.setItem(PRACTICE_FLAG_KEY, "1");
    } catch {
      // sessionStorage 不可用 → 仍回傳 true（旗標由 build 決定）
    }
    return true;
  }
  // URL 帶 ?practice=1 視為進入練習模式（並寫進 sessionStorage 以跨導頁保留）。
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("practice");
    if (fromUrl === "1") {
      sessionStorage.setItem(PRACTICE_FLAG_KEY, "1");
    }
  } catch {
    // 忽略：取不到 URL 參數就只看 sessionStorage
  }
  return sessionStorage.getItem(PRACTICE_FLAG_KEY) === "1";
};

export const getPracticeScenario = (): PracticeScenario => {
  if (!isBrowser()) return "normal";
  const raw = sessionStorage.getItem(PRACTICE_SCENARIO_KEY) as PracticeScenario | null;
  return PRACTICE_SCENARIOS.some((s) => s.key === raw) ? (raw as PracticeScenario) : "normal";
};

// --- React 鏡射層 ---------------------------------------------------------

interface PracticeStoreState {
  active: boolean;
  scenario: PracticeScenario;
  setActive: (active: boolean) => void;
  setScenario: (scenario: PracticeScenario) => void;
}

export const usePracticeStore = create<PracticeStoreState>((set) => ({
  active: isPracticeMode(),
  scenario: getPracticeScenario(),
  setActive: (active) => set({ active }),
  setScenario: (scenario) => set({ scenario }),
}));

/** 進入練習模式並設定情境（寫 sessionStorage + 同步 zustand）。 */
export const enablePractice = (scenario: PracticeScenario) => {
  if (isBrowser()) {
    sessionStorage.setItem(PRACTICE_FLAG_KEY, "1");
    sessionStorage.setItem(PRACTICE_SCENARIO_KEY, scenario);
  }
  usePracticeStore.getState().setActive(true);
  usePracticeStore.getState().setScenario(scenario);
};

export const setPracticeScenario = (scenario: PracticeScenario) => {
  if (isBrowser()) sessionStorage.setItem(PRACTICE_SCENARIO_KEY, scenario);
  usePracticeStore.getState().setScenario(scenario);
};

/** 離開練習模式：清除所有練習相關的 session 殘留，回到乾淨狀態。 */
export const exitPractice = () => {
  if (isBrowser()) {
    sessionStorage.removeItem(PRACTICE_FLAG_KEY);
    sessionStorage.removeItem(PRACTICE_SCENARIO_KEY);
    sessionStorage.removeItem("jwtToken");
    sessionStorage.removeItem("lineAccessToken");
    sessionStorage.removeItem("invite");
    sessionStorage.removeItem("family_invite_code");
    sessionStorage.removeItem("gasShop_autoPopulatedFromLastOrder");
    sessionStorage.removeItem(PRACTICE_CUSTOMER_NAME_KEY);
  }
  usePracticeStore.getState().setActive(false);
};
